// ── Admin → Companies: the country features card (multi-tenant package "cpackdocs") ──────────────
// Read-only view of what the workspace country's pack sets for documents and operations (paper,
// fonts, land units, address divisions, portals, immigration services, statement readers, statutory
// book names), plus the one workspace override: which interface languages the menus offer.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cpackdocsApi, CountryFeaturesAdmin } from '../../utils/api/cpackdocs';
import { useStore } from '../../store';

const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 20, marginBottom: 16 };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' };
const btn: React.CSSProperties = { padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const FEATURES = ['paperSize', 'docFonts', 'landAreaUnits', 'addressParts', 'govPortals', 'immigration', 'bankParsers', 'statutoryBooks'];
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every(x => b.includes(x));

export default function CountryFeaturesCard() {
  const { t } = useTranslation();
  const loadMe = useStore(s => s.loadMe);
  const [d, setD] = useState<CountryFeaturesAdmin | null>(null);
  const [langs, setLangs] = useState<string[]>([]);
  const [err, setErr] = useState(''); const [msg, setMsg] = useState(''); const [saving, setSaving] = useState(false);
  const take = (x: CountryFeaturesAdmin) => { setD(x); setLangs(x.uiLanguages); };
  useEffect(() => { cpackdocsApi.adminGet().then(take).catch((e: any) => setErr(e.message)); }, []);
  if (!d) return err ? <div style={{ ...card, fontSize: 12, color: '#E24B4A' }}>{err}</div> : null;

  const countryDefault = d.packLanguages.length ? d.packLanguages : d.shippedLanguages;
  const toggle = (l: string) => setLangs(prev => (prev.includes(l) ? prev.filter(x => x !== l) : d.shippedLanguages.filter(x => x === l || prev.includes(x))));
  const save = async () => {
    if (!langs.length) { setErr(t('cpd_langs_need_one')); return; }
    setSaving(true); setErr(''); setMsg('');
    try {
      take(await cpackdocsApi.adminSetLanguages(sameSet(langs, countryDefault) ? [] : langs));
      loadMe().catch(() => { /* the menus pick it up on the next load */ });
      setMsg(t('cs_saved')); setTimeout(() => setMsg(''), 2000);
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  const show = (v: string | string[]) => (Array.isArray(v) ? (v.length ? v.join(' · ') : t('cpd_none')) : (v || t('cpd_none')));

  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('cpd_title')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>{t('cpd_hint')}</div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>{err}</div>}
      <div style={{ fontSize: 12.5, marginBottom: 10 }}>
        <span style={lbl}>{t('cpd_country')}: </span>
        {d.country ? <b>{d.countryName || d.country} ({d.country})</b> : <span style={{ color: 'var(--text-tertiary)' }}>{t('cpd_country_unset')}</span>}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <tbody>
          {FEATURES.filter(k => d.features[k]).map(k => (
            <tr key={k} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '6px 8px 6px 0', width: 170, color: 'var(--text-secondary)', verticalAlign: 'top' }}>{t(`cpd_f_${k}`)}</td>
              <td style={{ padding: '6px 0', color: 'var(--text-primary)' }}>{show(d.features[k].value)}</td>
              <td style={{ padding: '6px 0 6px 8px', whiteSpace: 'nowrap', textAlign: 'right', fontSize: 11, color: d.features[k].fromPack ? '#1D9E75' : 'var(--text-tertiary)' }}>
                {d.features[k].fromPack ? t('cpd_from_pack') : t('cpd_default')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '16px 0 2px' }}>{t('cpd_langs')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t('cpd_langs_hint')}</div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {d.shippedLanguages.map(l => (
          <label key={l} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={langs.includes(l)} onChange={() => toggle(l)} />{t(`cpd_lang_${l}`)}
          </label>
        ))}
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{d.uiLanguagesOverride.length ? t('cpd_langs_custom') : t('cpd_langs_follow')}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 14 }}>
        {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
        <button onClick={save} disabled={saving} style={btn}>{saving ? t('login_wait') : t('save')}</button>
      </div>
    </div>
  );
}
